import React, { useState, useEffect } from "react";
import { t } from "components/CentralDataMangement/translation/Translation";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import ModalPopup from "components/common/atoms/ModalPopup";
import Button from "components/common/atoms/Button";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import SignatoryTable from "./SignatoryTable";
import "./signatory-details.css";
import { validateRequired, validateSelectField, validateForm } from "components/common/services/ValidationService";
import { ValidationRules } from "components/common/utlis/TypeAnnotations";

interface SignatoryModalProps {
    show: boolean;
    onClose: () => void;
    onSave: (data: any[]) => void;
    virtueList: any[];
    signatoryList: any[];
}

const SignatoryModal: React.FC<SignatoryModalProps> = ({
    show,
    onClose,
    onSave,
    virtueList,
    signatoryList,
}) => {
    const [formData, setFormData] = useState<{
        name: string;
        position: string;
        pursuant: any;
    }>({
        name: "",
        position: "",
        pursuant: null,
    });

    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [localSignatoryList, setLocalSignatoryList] = useState<any[]>([]);
    const [fieldErrors, setFieldErrors] = useState<{
        name?: string;
        position?: string;
        pursuant?: string;
    }>({});

    useEffect(() => {
        if (show) {
            setLocalSignatoryList([...signatoryList]);
        }
    }, [show, signatoryList]);

    const validation = (
        name: string,
        value: any,
        isSingleFieldValidation: boolean = false
    ) => {
        const validationRules: ValidationRules = {
            name: [validateRequired],
            // position: [validateSelectField],
            position: [validateRequired],
            pursuant: [validateSelectField],
        };

        const validationErrors = validateForm(
            { ...formData, [name]: value },
            validationRules,
            isSingleFieldValidation ? name : undefined
        );

        if (isSingleFieldValidation && Object.keys(fieldErrors).length > 0) {
            setFieldErrors((prevErrors) => ({
                ...prevErrors,
                [name]: validationErrors[name],
            }));
        } else {
            setFieldErrors(validationErrors);
        }

        return Object.keys(validationErrors).length === 0;
    };

    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        validation(name, value, true);
    };

    const handleSelectChange = (selectedOption: any, fieldName: string) => {
        setFormData((prev) => ({ ...prev, [fieldName]: selectedOption }));
        validation(fieldName, selectedOption, true);
    };

    const handleEdit = (index: number) => {
        const signatory = localSignatoryList[index];
        const pursuant = virtueList.find((virtue) => virtue.value === signatory.pursuant);

        setFormData({
            name: signatory.name,
            position: signatory.position || "",
            pursuant: pursuant || null,
        });
        setEditIndex(index);
        setFieldErrors({});
    };

    const handleDelete = (index: number) => {
        setLocalSignatoryList(localSignatoryList.filter((_, i) => i !== index));
    };

    const handleSaveSignatory = async (e: React.FormEvent) => {
        e.preventDefault();
        const { name, value } = e.target as HTMLInputElement;
        if (!validation(name, value)) {
            return;
        }

        const isDuplicate = localSignatoryList.some(
            (sig, idx) => 
                idx !== editIndex && 
                sig.name.toLowerCase().trim() === formData.name.toLowerCase().trim()
        );

        if (isDuplicate) {
            setFieldErrors({ ...fieldErrors, name: "This name already exists" });
            return;
        }

        const hasActiveSignatory = localSignatoryList.some(sig => sig.status === 1);
        const isEditing = editIndex !== null;
        
        const newSignatory = {
            id: isEditing ? localSignatoryList[editIndex].id : null,
            name: formData.name,
            position: formData.position.trim(),
            pursuant: formData.pursuant?.value || null,
            status: isEditing || !hasActiveSignatory ? 1 : 0
        };

        const updatedList = isEditing
            ? localSignatoryList.map((sig, idx) => idx === editIndex ? newSignatory : { ...sig, status: 0 })
            : hasActiveSignatory
                ? [...localSignatoryList, newSignatory]
                : [...localSignatoryList.map(sig => ({ ...sig, status: 0 })), newSignatory];

        setLocalSignatoryList(updatedList);
        resetForm();
    };

    const resetForm = () => {
        setFormData({ name: "", position: "", pursuant: null });
        setEditIndex(null);
        setFieldErrors({});
    };

    const handleClose = () => {
        onSave(localSignatoryList);
        resetForm();
        onClose();
    };

    return (
        <ModalPopup
            show={show}
            onHide={handleClose}
            title={t("Signatory details")}
            body={
                <div>
                    <div className="signatory-table-wrapper">
                        <SignatoryTable
                            data={localSignatoryList}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            virtueList={virtueList}
                        />
                    </div>

                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <LabelWithInputField
                                name="name"
                                label={t("Name")}
                                handleChange={handleFieldChange}
                                type="text"
                                value={formData.name}
                                isMandatory={true}
                                placeholder="Name"
                                error={fieldErrors.name}
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <LabelWithInputField
                                name="position"
                                label={t("Position")}
                                handleChange={handleFieldChange}
                                type="text"
                                value={formData.position}
                                isMandatory={true}
                                placeholder="Position"
                                error={fieldErrors.position}
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <SelectWithSearch
                                title={t("Pursuant")}
                                name="pursuant"
                                isMandatory={true}
                                search={true}
                                options={virtueList}
                                value={formData.pursuant}
                                onChange={(e) => handleSelectChange(e, "pursuant")}
                                isMulti={false}
                                isTranslate={true}
                                error={fieldErrors.pursuant}
                            />
                        </div>
                        <div className="col-12 d-flex justify-content-end">
                            <Button
                                handleClick={handleClose}
                                className="modal-cancel-btn shadow-none px-4 me-2"
                            >
                                {t("Close")}
                            </Button>
                            <Button
                                handleClick={handleSaveSignatory}
                                className="modal-confirm-btn shadow-none px-4"
                            >
                                {t("Add")}
                            </Button>
                        </div>
                    </div>
                </div>
            }
            className="modal-signatory-details"
            style={{
                maxWidth: "98vw",
                width: "98vw",
                "--bs-modal-width": "98vw",
                "--bs-modal-max-width": "98vw",
                margin: "1vh auto",
            }}
        />
    );
};

export default SignatoryModal;
